import {
  Alert,
  Anchor,
  Badge,
  Card,
  Center,
  Group,
  Loader,
  Progress,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import { BarChart } from '@mantine/charts';
import {
  IconAlertTriangle,
  IconCalendarEvent,
  IconClock,
  IconPlaneTilt,
  IconUsers,
} from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import dayjs from 'dayjs';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { api, unwrap } from '../../api/client';
import { useSession } from '../../app/session';
import { EmptyState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { StatusBadge } from '../../components/StatusBadge';
import { formatDate } from '../../lib/format';

function Tile({
  label,
  value,
  hint,
  icon,
  color = 'aero',
  to,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  icon: ReactNode;
  color?: string;
  to?: string;
}) {
  const body = (
    <Card p="md" h="100%">
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Stack gap={2}>
          <Text fz="xs" tt="uppercase" c="dimmed" fw={600}>
            {label}
          </Text>
          <Text fz={28} fw={700} lh={1.1}>
            {value}
          </Text>
          {hint ? (
            <Text fz="xs" c="dimmed">
              {hint}
            </Text>
          ) : null}
        </Stack>
        <ThemeIcon variant="light" color={color} size={38} radius="md">
          {icon}
        </ThemeIcon>
      </Group>
    </Card>
  );

  if (!to) return body;
  return (
    <Anchor component={Link} to={to} underline="never" h="100%">
      {body}
    </Anchor>
  );
}

export function DashboardPage() {
  const { t } = useTranslation();
  const { organisation, can } = useSession();
  const org = organisation!.slug;

  const dashboard = useQuery({
    queryKey: ['dashboard', org],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/organisations/{slug}/dashboard/', {
          params: { path: { slug: org } },
        }),
      ),
  });

  const recentFlights = useQuery({
    queryKey: ['dashboard', 'flights', org],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/flights/', {
          params: { query: { ordering: '-date,-departure_time', page_size: 8 } },
        }),
      ),
    enabled: can('logbook.view'),
  });

  const expiring = useQuery({
    queryKey: ['dashboard', 'expiring', org],
    queryFn: async () => unwrap(await api.GET('/api/v1/people/expiring-documents/')),
    enabled: can('staff.view'),
  });

  const monthly = useQuery({
    queryKey: ['dashboard', 'monthly', org],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/flights/', {
          params: {
            query: {
              date_from: dayjs().subtract(11, 'month').startOf('month').format('YYYY-MM-DD'),
              page_size: 2000,
              ordering: 'date',
            },
          },
        }),
      ),
    enabled: can('logbook.view'),
  });

  if (dashboard.isLoading) {
    return (
      <Center mih={400}>
        <Loader />
      </Center>
    );
  }

  const data = dashboard.data;
  const fleet = data?.fleet;
  const flights = data?.flights;
  const alerts = data?.alerts;
  const maintenance = data?.maintenance;
  const people = data?.people;

  // Twelve months of flying, bucketed client-side: the logbook page is already paged for this
  // range and a second aggregation endpoint would only repeat it.
  const byMonth = (() => {
    const buckets = new Map<string, number>();
    for (let index = 11; index >= 0; index -= 1) {
      buckets.set(dayjs().subtract(index, 'month').format('YYYY-MM'), 0);
    }
    for (const flight of monthly.data?.results ?? []) {
      const key = dayjs(flight.date).format('YYYY-MM');
      if (!buckets.has(key)) continue;
      const [hours, minutes] = flight.total_time.split(':').map(Number);
      buckets.set(key, (buckets.get(key) ?? 0) + hours + minutes / 60);
    }
    return Array.from(buckets, ([month, hours]) => ({
      month: dayjs(`${month}-01`).format('MMM'),
      hours: Number(hours.toFixed(1)),
    }));
  })();

  const criticalCount = alerts?.critical ?? 0;
  const overdueCount = maintenance?.overdue ?? 0;

  return (
    <Stack gap="lg">
      <PageHeader
        title={data?.organisation.display_name ?? t('dashboard.title')}
        subtitle={t('dashboard.title')}
        badge={
          data?.organisation.kind ? (
            <Badge variant="light">{t(`organisation.${data.organisation.kind}`)}</Badge>
          ) : null
        }
      />

      {criticalCount > 0 || overdueCount > 0 ? (
        <Alert color="red" variant="light" icon={<IconAlertTriangle size={18} />}>
          <Group gap="lg">
            {criticalCount > 0 ? (
              <Anchor component={Link} to={`/${org}/alerts`} c="red" fw={600}>
                {t('dashboard.openAlerts')}: {criticalCount}
              </Anchor>
            ) : null}
            {overdueCount > 0 ? (
              <Anchor component={Link} to={`/${org}/maintenance`} c="red" fw={600}>
                {t('dashboard.overdueMaintenance')}: {overdueCount}
              </Anchor>
            ) : null}
          </Group>
        </Alert>
      ) : null}

      <SimpleGrid cols={{ base: 1, xs: 2, md: 4 }} spacing="md">
        <Tile
          label={t('dashboard.fleetStatus')}
          value={`${fleet?.airworthy ?? 0}/${fleet?.total ?? 0}`}
          hint={
            <Group gap={6}>
              {fleet?.in_maintenance ? (
                <StatusBadge kind="aircraft" value="IN_MAINTENANCE" size="xs" />
              ) : null}
              {fleet?.grounded ? (
                <StatusBadge kind="aircraft" value="GROUNDED" size="xs" />
              ) : null}
              {!fleet?.in_maintenance && !fleet?.grounded ? t('dashboard.airworthy') : null}
            </Group>
          }
          icon={<IconPlaneTilt size={20} />}
          color="teal"
          to={`/${org}/aircraft`}
        />
        <Tile
          label={t('dashboard.flightsThisYear')}
          value={flights?.total_display ?? '0:00'}
          hint={`${flights?.total ?? 0} × ${t('logbook.title').toLowerCase()}`}
          icon={<IconClock size={20} />}
          to={`/${org}/logbook`}
        />
        <Tile
          label={t('dashboard.openAlerts')}
          value={alerts?.open ?? 0}
          hint={
            alerts?.critical
              ? `${alerts.critical} ${t('alerts.CRITICAL').toLowerCase()}`
              : t('alerts.noAlerts')
          }
          icon={<IconAlertTriangle size={20} />}
          color={alerts?.critical ? 'red' : alerts?.open ? 'orange' : 'gray'}
          to={`/${org}/alerts`}
        />
        <Tile
          label={t('dashboard.people')}
          value={people?.total ?? 0}
          hint={`${people?.pilots ?? 0} ${t('dashboard.pilots').toLowerCase()}, ${
            people?.students ?? 0
          } ${t('dashboard.students').toLowerCase()}`}
          icon={<IconUsers size={20} />}
          color="grape"
          to={`/${org}/staff`}
        />
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
        <Card p="md">
          <Title order={3} mb="sm">
            {t('dashboard.monthlyFlying')}
          </Title>
          {monthly.isLoading ? (
            <Center h={220}>
              <Loader size="sm" />
            </Center>
          ) : byMonth.every((row) => row.hours === 0) ? (
            <EmptyState title={t('dashboard.noFlightsYet')} icon={<IconClock size={22} />} />
          ) : (
            <BarChart
              h={220}
              data={byMonth}
              dataKey="month"
              series={[{ name: 'hours', color: 'aero.6', label: t('common.hours') }]}
              tickLine="y"
              gridAxis="y"
              withBarValueLabel
            />
          )}
        </Card>

        <Card p="md">
          <Group justify="space-between" mb="sm">
            <Title order={3}>{t('dashboard.overdueMaintenance')}</Title>
            <Anchor component={Link} to={`/${org}/maintenance`} fz="sm">
              {t('action.viewAll')}
            </Anchor>
          </Group>
          <Stack gap="sm">
            {[
              { label: t('maintenance.OVERDUE'), value: maintenance?.overdue ?? 0, color: 'red' },
              { label: t('maintenance.DUE'), value: maintenance?.due ?? 0, color: 'orange' },
              {
                label: t('maintenance.DUE_SOON'),
                value: maintenance?.due_soon ?? 0,
                color: 'yellow',
              },
            ].map((row) => {
              const total =
                (maintenance?.overdue ?? 0) + (maintenance?.due ?? 0) + (maintenance?.due_soon ?? 0);
              return (
                <div key={row.label}>
                  <Group justify="space-between" mb={4}>
                    <Text fz="sm">{row.label}</Text>
                    <Text fz="sm" fw={600}>
                      {row.value}
                    </Text>
                  </Group>
                  <Progress
                    value={total ? (row.value / total) * 100 : 0}
                    color={row.color}
                    size="sm"
                    radius="xl"
                  />
                </div>
              );
            })}
            {!maintenance?.overdue && !maintenance?.due && !maintenance?.due_soon ? (
              <Text fz="sm" c="dimmed">
                {t('maintenance.OK')}
              </Text>
            ) : null}
          </Stack>
        </Card>
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
        {can('logbook.view') ? (
          <Card p="md">
            <Group justify="space-between" mb="sm">
              <Title order={3}>{t('dashboard.recentFlights')}</Title>
              <Anchor component={Link} to={`/${org}/logbook`} fz="sm">
                {t('action.viewAll')}
              </Anchor>
            </Group>
            {recentFlights.isLoading ? (
              <Center h={140}>
                <Loader size="sm" />
              </Center>
            ) : (recentFlights.data?.results ?? []).length === 0 ? (
              <EmptyState title={t('dashboard.noFlightsYet')} icon={<IconClock size={22} />} />
            ) : (
              <Stack gap={6}>
                {(recentFlights.data?.results ?? []).map((flight) => (
                  <Group key={flight.id} justify="space-between" wrap="nowrap" gap="xs">
                    <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
                      <Text fz="xs" c="dimmed" w={72} style={{ flexShrink: 0 }}>
                        {formatDate(flight.date)}
                      </Text>
                      <Text fz="sm" className="identifier" style={{ flexShrink: 0 }}>
                        {flight.registration}
                      </Text>
                      <Text fz="sm" className="pic-name" truncate="end">
                        {flight.pic_name}
                      </Text>
                    </Group>
                    <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
                      <Text fz="xs" c="dimmed">
                        {flight.departure_place}–{flight.arrival_place}
                      </Text>
                      <Text fz="sm" fw={600} className="numeric-cell" w={48}>
                        {flight.total_time}
                      </Text>
                    </Group>
                  </Group>
                ))}
              </Stack>
            )}
            {flights?.uncertified ? (
              <Alert color="blue" variant="light" mt="sm" fz="sm">
                {t('logbook.pendingCertification', { count: flights.uncertified })}
              </Alert>
            ) : null}
          </Card>
        ) : null}

        {can('staff.view') ? (
          <Card p="md">
            <Group justify="space-between" mb="sm">
              <Title order={3}>{t('dashboard.expiringDocuments')}</Title>
              <Anchor component={Link} to={`/${org}/staff`} fz="sm">
                {t('action.viewAll')}
              </Anchor>
            </Group>
            {expiring.isLoading ? (
              <Center h={140}>
                <Loader size="sm" />
              </Center>
            ) : (expiring.data ?? []).length === 0 ? (
              <EmptyState
                title={t('staff.VALID')}
                hint={t('alerts.noAlerts')}
                icon={<IconCalendarEvent size={22} />}
              />
            ) : (
              <Stack gap={6}>
                {(expiring.data ?? []).slice(0, 8).map((document, index) => (
                  <Group
                    key={`${document.person}-${document.kind}-${index}`}
                    justify="space-between"
                    wrap="nowrap"
                    gap="xs"
                  >
                    <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
                      <StatusBadge kind="validity" value={document.status} size="xs" />
                      <Text fz="sm" truncate="end">
                        {document.person_name}
                      </Text>
                      <Text fz="xs" c="dimmed" truncate="end">
                        {document.name}
                      </Text>
                    </Group>
                    <Text fz="xs" c="dimmed" style={{ flexShrink: 0 }}>
                      {formatDate(document.valid_until)}
                    </Text>
                  </Group>
                ))}
              </Stack>
            )}
          </Card>
        ) : null}
      </SimpleGrid>
    </Stack>
  );
}
